import React from 'react'

interface CongratulatoryModalProps {
  isOpen: boolean
  onClose: () => void
  completedDailyGoal: boolean
  totalReviewedToday: number
  dailyTarget: number
  uniqueCardsReviewed: number
}

export function CongratulatoryModal({
  isOpen,
  onClose,
  completedDailyGoal,
  totalReviewedToday,
  dailyTarget,
  uniqueCardsReviewed
}: CongratulatoryModalProps) {
  if (!isOpen) return null

  const safeTarget = Math.max(dailyTarget, 1)
  const progressPercentage = Math.min(100, Math.round((totalReviewedToday / safeTarget) * 100))
  const cardsRemaining = Math.max(dailyTarget - totalReviewedToday, 0)
  const encouragementMessage = completedDailyGoal
    ? "Way to go! Today's practice streak is locked in."
    : cardsRemaining === 0
      ? 'You matched your goal - anything extra is a bonus!'
      : `Only ${cardsRemaining} more ${cardsRemaining === 1 ? 'card' : 'cards'} to reach your goal.`
  const tokoNote = completedDailyGoal
    ? 'Toko is twirling on the lily pad just for you.'
    : 'Toko says every review counts - keep the rhythm going.'
  const actionLabel = completedDailyGoal ? 'Celebrate and continue' : 'Keep going'
  const accentGradient = completedDailyGoal
    ? 'from-primary-500 to-secondary-500'
    : 'from-primary-400 to-primary-600'
  const stats = [
    { label: 'Cards Reviewed', value: totalReviewedToday },
    { label: 'Daily Target', value: dailyTarget },
    { label: 'Unique Cards', value: uniqueCardsReviewed }
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[var(--color-surface)] rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4 animate-scale-in border border-[var(--color-border)] overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <span className="celebration-orb celebration-orb--primary" />
          <span className="celebration-orb celebration-orb--secondary" />
          <span className="celebration-confetti celebration-confetti--one" />
          <span className="celebration-confetti celebration-confetti--two" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-primary-200/20 via-transparent to-secondary-200/20 pointer-events-none" />
        <div className="relative">
          {/* Toko avatar */}
          <div className="flex justify-center -mt-20 mb-4 celebration-avatar">
            <div className="relative">
              <div className="absolute inset-0 blur-2xl bg-primary-400/30 animate-pulse-soft" />
              <div className="relative w-24 h-24 rounded-full bg-[var(--color-surface-secondary)] border border-[var(--color-border)] shadow-lg flex items-center justify-center">
                <img
                  src="/frogicon.png"
                  alt="Toko cheering you on"
                  className="w-14 h-14"
                  draggable={false}
                />
              </div>
            </div>
          </div>

          <div className="text-center mb-6">
            <p className="text-sm uppercase tracking-wide text-[var(--color-text-secondary)]">
              {completedDailyGoal ? 'Goal complete' : 'Daily review'}
            </p>
            <h2 className="text-3xl font-semibold text-[var(--color-text)] mt-2">
              {completedDailyGoal ? 'You did it!' : 'Great work today!'}
            </h2>
            <p className="text-[var(--color-text-secondary)] mt-3" aria-live="polite">
              {encouragementMessage}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-secondary)]/70 p-4 text-center"
              >
                <p className="text-sm text-[var(--color-text-secondary)]">{stat.label}</p>
                <p className="text-2xl font-semibold text-[var(--color-text)] mt-1">
                  {stat.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between text-sm text-[var(--color-text-secondary)] mb-2">
              <span>Progress toward goal</span>
              <span>{progressPercentage}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-[var(--color-border)]/50 overflow-hidden">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${accentGradient}`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          <div className="bg-[var(--color-surface-secondary)]/50 border border-[var(--color-border)] rounded-xl p-4 mb-8 text-center">
            <p className="text-sm font-medium text-[var(--color-text)]">
              {tokoNote}
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-[var(--color-surface)]"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
